import React, { useEffect, useState } from 'react';
import { Offcanvas, Form, Button, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import API_BASE_URL from '../../../config/api';
import { Box, Divider, Typography } from "@mui/material";
import {
  PersonPlusFill,
  ShieldLockFill,
  GeoAltFill,
  ImageFill,
  EnvelopeFill,
  KeyFill
} from "react-bootstrap-icons";

// Reusable SectionHeader component
const SectionHeader = ({ icon, label }) => (
  <Box display="flex" alignItems="center" gap={1} mb={2} mt={2}>
    <Box sx={{ color: "#0d47a1", display: "flex" }}>{icon}</Box>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "#7b8ab8",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Typography>
    <Divider sx={{ flex: 1, borderColor: "#dde4f5" }} />
  </Box>
);

// Reusable field style
const fieldStyle = {
  borderRadius: "8px",
  border: "1.5px solid #dde4f5",
  fontSize: "0.875rem",
  padding: "10px",
};

const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#1a237e" };

const AddInstructorOffcanvas = ({ show, handleClose, onInstructorAdded }) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting }
  } = useForm();
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/location/admin/getAllLocations`);
        
        // Filter to only include active locations
        const activeLocations = (response.data?.data || [])
          .filter(location => location.status === 'Active');
        
        setLocations(activeLocations);
      } catch (error) {
        console.error('Error fetching locations:', error);
        toast.error('Failed to load locations');
      }
    };

    fetchLocations();
  }, []);

  const onSubmit = async (data) => {
    const token = localStorage.getItem('adminToken');
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('email', data.email);
    formData.append('password', data.password);
    formData.append('confirmPassword', data.confirmPassword);
    formData.append('location', data.location);
    formData.append('files', data.files[0]); // single file
    formData.append('prisonerid', '123456789');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/adminRegister/instructor`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Instructor added successfully!');
      onInstructorAdded();
      handleClose();
      reset();
    } catch (err) {
      toast.error('Error: ' + (err.response?.data?.message || err.message));
    }
  };

  const password = watch('password');

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      style={{ width: 450 }}
    >
      <Offcanvas.Header
        closeButton
        className="text-white"
        style={{
          background: "linear-gradient(135deg, #1a237e, #0d47a1)",
          borderBottom: "none",
        }}
      >
        <Offcanvas.Title
          className="d-flex align-items-center gap-2 fw-bold"
          style={{ fontSize: "1rem" }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: "rgba(255,235,59,0.2)",
              border: "1.5px solid rgba(255,235,59,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PersonPlusFill size={13} color="#ffeb3b" />
          </Box>
          Add New Instructor
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body style={{ background: "#f0f4ff" }}>
        <Form onSubmit={handleSubmit(onSubmit)} className="p-3 rounded-3 shadow-sm bg-white">
          
          <SectionHeader
            icon={<PersonPlusFill size={13} />}
            label="Personal Information"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Full Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              {...register('name', { required: 'Name is required' })}
              placeholder="Enter full name"
              style={fieldStyle}
              isInvalid={!!errors.name}
            />
            {errors.name && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.name.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Email Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              {...register('email', { 
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              placeholder="Enter email address"
              style={fieldStyle}
              isInvalid={!!errors.email}
            />
            {errors.email && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.email.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader
            icon={<ShieldLockFill size={13} />}
            label="Security Credentials"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="password"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 6, message: 'Password must be at least 6 characters' }
              })}
              placeholder="Enter password (min. 6 characters)"
              style={fieldStyle}
              isInvalid={!!errors.password}
            />
            {errors.password && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.password.message}
              </div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Confirm Password <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="password"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match'
              })}
              placeholder="Re-enter password"
              style={fieldStyle}
              isInvalid={!!errors.confirmPassword}
            />
            {errors.confirmPassword && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.confirmPassword.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader
            icon={<GeoAltFill size={13} />}
            label="Assignment"
          />

          <Form.Group className="mb-3">
            <Form.Label style={labelStyle}>
              Location <span className="text-danger">*</span>
            </Form.Label>
            <Form.Select
              {...register('location', { required: 'Location is required' })}
              style={fieldStyle}
              isInvalid={!!errors.location}
            >
              <option value="">Select location</option>
              {locations.map((loc) => (
                <option key={loc._id} value={loc._id}>
                  {loc.location}
                </option>
              ))}
            </Form.Select>
            {errors.location && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.location.message}
              </div>
            )}
          </Form.Group>

          <SectionHeader
            icon={<ImageFill size={13} />}
            label="Profile Image"
          />

          <Form.Group className="mb-4">
            <Form.Label style={labelStyle}>Upload Profile Picture</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              {...register('files')}
              style={fieldStyle}
            />
            <Form.Text muted>Optional: Upload a profile image (JPG, PNG)</Form.Text>
            {errors.files && (
              <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>
                {errors.files.message}
              </div>
            )}
          </Form.Group>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-100 d-flex align-items-center justify-content-center gap-2 fw-bold"
            style={{
              background: "linear-gradient(135deg, #1a237e, #0d47a1)",
              border: "none",
              borderRadius: "10px",
              padding: "11px",
              boxShadow: "0 4px 14px rgba(13,71,161,0.3)",
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" animation="border" /> Adding Instructor...
              </>
            ) : (
              <>
                <PersonPlusFill size={15} /> Add Instructor
              </>
            )}
          </Button>
        </Form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AddInstructorOffcanvas;